import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const PortadownChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Treatment Portadown | Fibromyalgia Hypnotherapy Armagh</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Portadown from Belfast clinic (30 miles). Neuroscience approach for fibromyalgia, back pain, arthritis. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Portadown, fibromyalgia hypnotherapy Portadown, back pain treatment Portadown, arthritis pain Portadown, pain management Craigavon" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/portadown-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Treatment Portadown | Fibromyalgia Hypnotherapy Armagh" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Portadown. Neuroscience approach for fibromyalgia, back pain, arthritis." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Treatment Portadown",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Portadown and Craigavon from Belfast clinic. Specializing in fibromyalgia, chronic back pain, arthritis, migraines, and neuropathic pain with NHS-complementary approach. Supporting commercial hub community with 15 years nursing experience.",
            "url": "https://www.sharonclare-hypnotherapy.com/portadown-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Portadown",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Portadown from Belfast clinic?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is approximately 30 miles from Portadown via the M1 motorway, taking 40-45 minutes by car in normal traffic, and the clinic is then a short run south along the Ormeau Road. This manageable distance makes regular pain management appointments practical. Alternatively, Portadown is on the Belfast rail line, with Botanic and City Hospital the closest stations to the clinic, providing another travel option for those who prefer not to drive."
                }
              },
              {
                "@type": "Question",
                "name": "Can pain treatment be done online?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Portadown residents who prefer online treatment or cannot make the drive to Belfast during pain flare-ups. Online sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. This option is ideal for clients with mobility limitations, those experiencing severe pain days, or anyone who wants maximum flexibility. You can switch between in-person and online sessions based on your needs."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for retail worker pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for retail worker pain from standing all day, repetitive movements, and customer-facing stress. Many Portadown retail workers at the Meadows or elsewhere experience chronic foot pain, back pain, leg pain, and tension headaches. When you combine the physical strain of standing for 8+ hours with customer stress and job pressure, your nervous system amplifies pain signals. Hypnotherapy calms this overactive alarm system, reduces muscle tension, and helps your brain process pain differently. Many retail workers report significant improvements in pain levels and ability to get through shifts."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with arthritis?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy can significantly help with arthritis pain management. While it doesn't change the joint damage itself, hypnotherapy works with how your brain processes arthritis pain. By calming your nervous system, reducing stress that worsens inflammation, improving sleep quality, and helping your brain turn down the volume on pain signals, many Portadown clients with osteoarthritis or rheumatoid arthritis experience meaningful reductions in pain intensity and improvements in mobility. This works alongside your NHS care and medications, not instead of them."
                }
              },
              {
                "@type": "Question",
                "name": "How does standing all day affect chronic pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Standing all day in retail or hospitality creates immense strain on feet, legs, back, and joints. Static standing (staying in one place) is particularly problematic because muscles stay contracted, blood pools in legs, and joints bear constant weight without movement breaks. This physical strain combined with customer-facing stress and limited control over your working conditions keeps your nervous system on high alert, which significantly amplifies pain perception. Many Portadown retail workers experience pain that worsens as the shift progresses because stress and physical fatigue compound. Hypnotherapy addresses both the nervous system amplification and provides pain management techniques you can use during your shift."
                }
              },
              {
                "@type": "Question",
                "name": "Can I continue my medications?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. Hypnotherapy works alongside your medications and other NHS treatments. Never stop or change medications without consulting your GP or pain specialist. With 15 years nursing experience, I understand pain medications and their role in pain management. Many Portadown clients find that as hypnotherapy helps reduce their pain levels over time, they can discuss with their GP whether medication doses might be reduced. But this decision is always made collaboratively with your medical team based on your progress and needs."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Management for Portadown
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Neuroscience-based hypnotherapy serving Portadown from Belfast
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">30 miles</span> from Portadown via M1 • <span className="font-semibold">40-45 min drive</span>
                </p>
              </div>
              <p className="text-gray-600">Train service and online sessions available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Portadown
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Portadown and the Craigavon area from our Belfast clinic, located approximately 30 miles northeast via the M1 motorway. The journey takes 40-45 minutes by car, then south along the Ormeau Road, or alternatively you can take the train from Portadown into Belfast and get off at Botanic or City Hospital, the closest stations to the clinic. For Portadown residents dealing with persistent pain conditions like fibromyalgia, chronic back pain, or arthritis, these manageable travel options provide access to specialist hypnotherapy that works alongside your NHS care.
              </p>
              <p>
                Living with chronic pain in Portadown comes with specific pressures that affect how pain feels. As the largest of Craigavon's twin towns and a significant commercial hub, Portadown has a strong retail and service sector economy. Many residents work in customer-facing roles at the Meadows Shopping Centre, in hospitality, or in retail throughout the town. These jobs typically involve standing all day, repetitive movements, dealing with customer demands, and shift work patterns. The physical strain of standing for 8+ hours combined with customer-facing stress and economic pressures creates a perfect storm for chronic pain conditions.
              </p>
              <p>
                Modern pain neuroscience shows us that chronic pain is processed in the nervous system, not just at the injury site. When we're stressed from demanding work conditions, customer interactions, financial concerns, or the accumulated fatigue of retail work, our brain's alarm system becomes hypersensitive and amplifies pain signals. This is why the same chronic pain can feel worse during busy shifts or stressful periods. This is where solution-focused hypnotherapy excels. By calming your overactive nervous system, teaching your brain healthier pain processing patterns, and addressing the stress that intensifies pain, many Portadown clients experience significant reductions in pain levels and improvements in ability to work and function daily.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I bring medical understanding to chronic pain treatment. I understand conditions like fibromyalgia, chronic back pain from standing, arthritis, and retail worker pain from both a clinical and therapeutic perspective. I know how frustrating it is when you're told to "just rest" when you can't afford to take time off work, or when medications help a bit but don't address the underlying problem. Hypnotherapy offers a science-backed approach that works alongside your medical care to help you manage pain better, function more effectively at work, and improve your quality of life. This isn't about ignoring the pain - it's about retraining your nervous system to respond differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Chronic Pain */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              The Neuroscience of Chronic Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Persistent pain lasting beyond 12 weeks isn't "all in your head," but it is in your nervous system. Understanding how pain works helps explain why hypnotherapy can be so effective.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Shield className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Central Sensitization</h3>
                <p className="text-gray-700">
                  The spinal cord and brain become hypersensitive, amplifying normal signals and interpreting them as pain. This is why chronic pain can feel disproportionate to actual tissue damage, and why customer stress or busy shifts can worsen pain intensity. Your nervous system's volume control is turned up too high.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Fight-Flight-Freeze</h3>
                <p className="text-gray-700">
                  Ongoing stress from demanding retail work, difficult customers, shift work fatigue, or economic pressures keeps your survival system on high alert. When your brain perceives threat - even from work stress or financial concerns - it keeps pain signals amplified as a protective mechanism. Calming this system is essential for pain reduction.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Neuroplasticity</h3>
                <p className="text-gray-700">
                  The brain learns pain pathways through repetition. The more they're used, the easier they fire - like a well-worn path. But neuroplasticity also means we can create new, healthier pathways. Your brain can learn to process pain differently, and hypnotherapy facilitates that relearning process.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                This is why you can feel real, distressing pain even when medical scans look "normal." The pain is genuine - your brain is simply protecting you a little too well. For Portadown retail and hospitality workers dealing with the physical demands of standing all day combined with customer-facing stress, this overprotective system often gets stuck in overdrive. Hypnotherapy works by calming this overactive alarm system and helping your brain interpret sensations differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy works directly with the mind-body connection to calm your overactive alarm system
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Shift from Danger to Safety</h3>
                    <p className="text-gray-700">
                      Calms the fight-flight response so your nervous system stops amplifying pain signals. This is particularly important for Portadown retail workers dealing with the physical demands of standing all day, customer stress, and the accumulated tension of service sector work where you must stay pleasant despite pain or fatigue.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Lowers the intensity of pain messages traveling through the spinal cord to the brain, reducing pain perception through descending pain inhibition pathways. Neuroscience research shows the brain can literally turn down the volume on pain signals when it feels safe and calm.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Harness Neuroplasticity</h3>
                    <p className="text-gray-700">
                      Creates new, healthier neural pathways so your brain can learn to interpret sensations differently. The brain's remarkable ability to change means chronic pain doesn't have to be permanent. We can retrain your nervous system to respond to pain signals in a healthier way.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Practical Self-Management Tools</h3>
                    <p className="text-gray-700">
                      Self-hypnosis and relaxation techniques you can use during your retail shift, at home, or during your commute to manage pain flare-ups, reduce stress, and improve sleep - all factors that significantly influence pain levels. These are practical tools for real life, not just the therapy room.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                Research supports hypnotherapy's role in multidisciplinary pain care. Studies show hypnotherapy can significantly reduce pain intensity for conditions like fibromyalgia, chronic back pain from standing, and arthritis by working with the brain's natural pain control mechanisms. A major review found hypnotherapy 68% more effective than no treatment for chronic pain conditions.
              </p>
              <p className="text-gray-700 text-lg">
                For Portadown residents waiting months for Southern Trust pain clinic appointments, hypnotherapy offers evidence-based support that can provide relief while you wait for NHS specialist care, or work alongside the treatment you're already receiving. The 40-45 minute drive or train journey to Belfast is manageable for the potential to significantly reduce your pain and improve your ability to work and function daily.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your Medical Care
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Southern Trust pain clinic waiting times often exceeding 12 months, many Portadown residents need support now while waiting for specialist appointments. Hypnotherapy can provide that bridge, offering relief and improved functioning while you wait for or work alongside NHS care.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I work collaboratively with your GP, pain specialists, physiotherapists, and other healthcare providers. Many chronic pain clinics now recommend hypnotherapy as part of a multidisciplinary approach alongside physiotherapy, medication, mindfulness, or gentle movement programs. By addressing the nervous system directly, hypnotherapy enhances these treatments and helps you regain confidence, mobility, and ability to work despite pain.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare team. I know the medications, the procedures, the specialist terminology, and the frustrations of chronic pain from both sides of the clinical encounter. This medical background means I understand when something needs medical attention versus when hypnotherapy can help, and I'll always support you in getting appropriate medical care.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Medical Background</h3>
                <p className="text-gray-700">15 years Belfast City Hospital nursing experience brings clinical credibility to pain management</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">CNHC Registered</h3>
                <p className="text-gray-700">Professional registration ensures you receive evidence-based, ethical treatment</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">GP Collaboration</h3>
                <p className="text-gray-700">I work with your GP and medical team, not in competition with them</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Evidence-Based Approach</h3>
                <p className="text-gray-700">Neuroscience-backed techniques supported by research, not pseudoscience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Portadown
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the M1 motorway northbound into Belfast, then south along the Ormeau Road (A24)
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> Approximately 30 miles
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 40-45 minutes in normal traffic
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Public transport:</span> Metro services run along the Ormeau Road corridor, with Botanic and City Hospital the nearest rail stations
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 mb-6">
                <Train className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Train</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Portadown into Belfast on the Belfast-Newry/Portadown line
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Journey Time:</span> Approximately 35-40 minutes
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">From Station:</span> Botanic and City Hospital are the closest stations to the clinic, with Lanyon Place for regional services
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Manageable Journey Options</h3>
                <p className="text-gray-700">
                  Portadown's position as a major railway junction on the Dublin-Belfast line provides excellent transport links. Whether you drive via the M1 or take the train, Belfast is accessible for regular pain management appointments. Many clients find the journey worthwhile for specialist care that can significantly reduce their pain levels and improve their ability to work and function. The investment in travel time often pays dividends in pain reduction and improved quality of life.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                If traveling is difficult due to your pain condition, mobility limitations, or during severe flare-ups, I offer secure online hypnotherapy sessions via video call. Online sessions provide the same professional treatment with the convenience of attending from your home in Portadown. This option is ideal for those experiencing high pain days, retail workers with irregular shift patterns, anyone with mobility concerns, or those who prefer the flexibility of online therapy. You can switch between in-person and online sessions based on your needs and pain levels.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Craigavon Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working retail at the Meadows, standing for 8+ hours a day, my back and leg pain was unbearable. I was taking painkillers constantly just to get through shifts. Sharon's hypnotherapy helped me understand how customer stress and physical strain were making everything worse. After 6 sessions, my pain levels dropped significantly. I can get through busy Saturday shifts now without agony. Her nursing background meant she really understood the physical demands of retail work."
                </p>
                <p className="font-semibold text-gray-900">R.K., Portadown - Retail Worker Chronic Pain</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My arthritis pain in my knees and hands was getting worse every year. The NHS had me on waiting lists but nothing was helping in the meantime. Sharon's approach helped calm my nervous system and the pain intensity decreased noticeably. I'm sleeping better, moving more easily, and I've been able to stay working. The train from Portadown to Belfast made appointments easy, and it was worth every journey for the improvements I've experienced."
                </p>
                <p className="font-semibold text-gray-900">M.T., Craigavon Borough - Arthritis</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with fibromyalgia for 8 years, I'd tried everything. The online hypnotherapy sessions with Sharon made a real difference when nothing else could. She helped me manage my pain in ways the medical system couldn't offer. I'm experiencing fewer flare-ups, my sleep has improved dramatically, and I feel like I have some control back over my life instead of the pain controlling me. Can't recommend her enough."
                </p>
                <p className="font-semibold text-gray-900">L.H., County Armagh - Fibromyalgia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Portadown Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Portadown from Belfast clinic?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 30 miles from Portadown via the M1 motorway, taking 40-45 minutes by car in normal traffic, and the clinic is then a short run south along the Ormeau Road. This manageable distance makes regular pain management appointments practical. Alternatively, Portadown is on the Belfast rail line, with Botanic and City Hospital the closest stations to the clinic, providing another travel option for those who prefer not to drive.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can pain treatment be done online?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Portadown residents who prefer online treatment or cannot make the drive to Belfast during pain flare-ups. Online sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. This option is ideal for clients with mobility limitations, those experiencing severe pain days, or anyone who wants maximum flexibility. You can switch between in-person and online sessions based on your needs.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for retail worker pain?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for retail worker pain from standing all day, repetitive movements, and customer-facing stress. Many Portadown retail workers at the Meadows or elsewhere experience chronic foot pain, back pain, leg pain, and tension headaches. When you combine the physical strain of standing for 8+ hours with customer stress and job pressure, your nervous system amplifies pain signals. Hypnotherapy calms this overactive alarm system, reduces muscle tension, and helps your brain process pain differently. Many retail workers report significant improvements in pain levels and ability to get through shifts.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with arthritis?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy can significantly help with arthritis pain management. While it doesn't change the joint damage itself, hypnotherapy works with how your brain processes arthritis pain. By calming your nervous system, reducing stress that worsens inflammation, improving sleep quality, and helping your brain turn down the volume on pain signals, many Portadown clients with osteoarthritis or rheumatoid arthritis experience meaningful reductions in pain intensity and improvements in mobility. This works alongside your NHS care and medications, not instead of them.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does standing all day affect chronic pain?
                </h3>
                <p className="text-gray-700">
                  Standing all day in retail or hospitality creates immense strain on feet, legs, back, and joints. Static standing (staying in one place) is particularly problematic because muscles stay contracted, blood pools in legs, and joints bear constant weight without movement breaks. This physical strain combined with customer-facing stress and limited control over your working conditions keeps your nervous system on high alert, which significantly amplifies pain perception. Many Portadown retail workers experience pain that worsens as the shift progresses because stress and physical fatigue compound. Hypnotherapy addresses both the nervous system amplification and provides pain management techniques you can use during your shift.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I continue my medications?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Hypnotherapy works alongside your medications and other NHS treatments. Never stop or change medications without consulting your GP or pain specialist. With 15 years nursing experience, I understand pain medications and their role in pain management. Many Portadown clients find that as hypnotherapy helps reduce their pain levels over time, they can discuss with their GP whether medication doses might be reduced. But this decision is always made collaboratively with your medical team based on your progress and needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore how clinical hypnotherapy can help with chronic pain, book your initial consultation today and start retraining your brain to turn down the volume on pain."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default PortadownChronicPain;
